<script lang="ts">
import {
  defineComponent,
  inject,
  computed,
  ref,
  getCurrentInstance,
  onMounted,
} from "vue";
import { Page } from "@/utils/enum";
import { useGoogleAnalytics, Event } from "@/composables/useGoogleAnalytics";
import {
  Contact,
  ContactCurrentUserPermissions,
  ContactGroup,
} from "@/models/dto/ContactGroup";
import CreateEditContact from "@/components/Contact/CreateEditContact.vue";
import { URMRoles } from "@/models/dto/userRoleManagement";
import quotes from "@/services/quotes";
import { ContactType, getDisabledUserRoles } from "./Contact/utils/helpers";
import DialogLayout from "@/components/common/DialogLayout.vue";
import ContactListItem from "@/components/Contact/ContactListItem.vue";
import { flatMap } from "lodash";
import ConfirmRemoveContactDialog from "@/components/Contact/ConfirmRemoveContactDialog.vue";
import ContactList from "@/components/Contact/ContactList.vue";

export default defineComponent({
  name: "ShareQuoteDialogV2",
  components: {
    CreateEditContact: CreateEditContact,
    DialogLayout: DialogLayout,
    ContactListItem: ContactListItem,
    ContactList: ContactList,
    ConfirmRemoveContactDialog: ConfirmRemoveContactDialog,
  },
  computed: {
    URMRoles() {
      return URMRoles;
    },
  },
  props: {
    quoteId: { type: Number, required: true },
  },
  setup(props) {
    const { proxy } = getCurrentInstance()!;
    const t = proxy.$t.bind(proxy);

    const contactGroups = ref<ContactGroup[]>([]);
    const currentUserCreatePermissions = ref<ContactCurrentUserPermissions>();
    const showCreateDialog = ref(false);
    const showUsersWithAccessDialog = ref(false);
    const analytics = useGoogleAnalytics();
    const isInEditMode = ref(false);
    const contactToEdit = ref<Contact | null>(null);
    const transitionName = ref("slide-right"); // default
    const isLoading = ref(true);

    const isInQuoteDetail = inject("isInQuoteDetail", false);
    const isInProvider = inject("isInProvider", false);
    const isInCheckout = inject("isInCheckout", false);
    const isInGuestCheckout = inject("isInGuestCheckout", false);
    const showRemoveContactDialog = ref(false);

    const page = computed(() => {
      if (isInQuoteDetail) {
        return Page.QuoteDetail;
      }
      if (isInProvider) {
        return Page.Provider;
      }
      if (isInCheckout || isInGuestCheckout) {
        return Page.Checkout;
      }
      return Page.Other;
    });

    const disabledUserRoles = computed(() => {
      return getDisabledUserRoles(currentUserCreatePermissions.value);
    });

    const trackShareQuote = () => {
      analytics.trackEvent(Event.ShareQuote, {
        page: page.value,
      });
    };

    const loadContactGroups = async (): Promise<void> => {
      try {
        const sharedListResponse = await quotes.getContacts(props.quoteId);
        contactGroups.value = sharedListResponse.data.contactGroups || [];
        currentUserCreatePermissions.value =
          sharedListResponse.data.currentUserCreatePermissions;
      } catch (error: any) {
        console.error(
          `Error loading contact groups for quote ${props.quoteId}`,
          error.response.data.message
        );
      } finally {
        isLoading.value = false;
      }
    };

    const onAddContact = async () => {
      trackShareQuote();
      showCreateDialog.value = true;
      await loadContactGroups();
      resetDialog();
    };

    const handleShowCreateDialogInput = (val: boolean) => {
      showCreateDialog.value = val;
      if (!val) {
        contactToEdit.value = null;
        isInEditMode.value = false;
      }
    };

    const handleShowUsersWithAccessDialogInput = (val: boolean) => {
      showUsersWithAccessDialog.value = val;
    };

    const tripAuthorizedUser = URMRoles.TripAuthorizedUser.key;

    const resetDialog = () => {
      contactToEdit.value = null;
      isInEditMode.value = false;
    };

    const handleEditContactClick = (contact: Contact) => {
      transitionName.value = "slide-left";
      contactToEdit.value = contact;
      isInEditMode.value = true;
    };

    const handleHeaderActionLeft = () => {
      transitionName.value = "slide-right";
      isInEditMode.value = false;
      contactToEdit.value = null;
    };

    const onUpdateContact = async () => {
      await loadContactGroups();
      resetDialog();
    };

    const allContacts = computed(() => {
      return flatMap(contactGroups.value, (group) => group.contacts);
    });

    const contactType = computed(() => {
      return ContactType.Quote;
    });

    onMounted(async () => {
      await loadContactGroups();
    });

    const handleRemoveDialogInput = (val: boolean) => {
      showRemoveContactDialog.value = val;
      if (!val) {
        contactToEdit.value = null;
      }
    };
    const handleRemoveContactCancel = () => {
      showRemoveContactDialog.value = false;
      showCreateDialog.value = true;
    };

    const handleRemoveContactClick = (contact: Contact) => {
      contactToEdit.value = contact;
      showRemoveContactDialog.value = true;
      showCreateDialog.value = false;
    };

    const onRemoveContact = async () => {
      await loadContactGroups();
      resetDialog();
      showCreateDialog.value = true;
    };

    return {
      t,
      allContacts,
      showCreateDialog,
      showUsersWithAccessDialog,
      showRemoveContactDialog,
      isInEditMode,
      contactGroups,
      tripAuthorizedUser,
      handleShowCreateDialogInput,
      handleShowUsersWithAccessDialogInput,
      onAddContact,
      resetDialog,
      handleEditContactClick,
      handleRemoveDialogInput,
      handleRemoveContactCancel,
      handleRemoveContactClick,
      handleHeaderActionLeft,
      onUpdateContact,
      currentUserCreatePermissions,
      disabledUserRoles,
      onRemoveContact,
      contactToEdit,
      transitionName,
      isLoading,
      contactType,
    };
  },
});
</script>

<template>
  <div id="share-quote-dialog-v2">
    <!-- Loader while permissions are loading -->
    <v-progress-circular
      v-if="isLoading"
      indeterminate
      color="primary"
      class="my-4"
    />
    <div v-if="!isLoading && contactGroups.length > 0">
      <CreateEditContact
        v-if="
          currentUserCreatePermissions?.canAddUser &&
          disabledUserRoles
        "
        :value="showCreateDialog"
        @input="handleShowCreateDialogInput"
        :title="$t('common.SHARE_QUOTE').toString()"
        subtitle="You can invite others to help manage this booking!"
        :submit-button-text="
          isInEditMode
            ? $t('common.UPDATE').toString()
            : $t('common.SEND_INVITE').toString()
        "
        :contact-groups="contactGroups"
        :contact="contactToEdit"
        :header-action-right-text="$t('common.CLOSE').toString()"
        :header-action-left-text="
          isInEditMode ? $t('common.BACK').toString() : ''
        "
        @click:header-action-left="handleHeaderActionLeft"
        message="You'll have the option to add a View-Only user to a reservation."
        :default-user-type="tripAuthorizedUser"
        :default-disabled-user-roles="disabledUserRoles"
        :is-edit-mode="isInEditMode"
        @click:edit-contact="handleEditContactClick"
        @click:remove-contact="handleRemoveContactClick"
        @update:contact="onUpdateContact"
        @add:contact="onAddContact"
        contactListIsEditable
        :contact-type="contactType"
        :entity-id="quoteId"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            id="share-quote-button"
            v-bind="attrs"
            small
            outlined
            color="primary"
            class="border-radius-8 padding-a-2 h-32 max-h-32"
            v-on="on"
          >
            <span class="d-flex align-center">
              <CUIcon view-box="0 0 24 24" class="w-16 h-16 margin-r-2">
                share
              </CUIcon>
              Share
            </span>
          </v-btn>
        </template>
      </CreateEditContact>

      <!-- Show users-with-access dialog if user cannot add -->
      <DialogLayout
        v-else
        :value="showUsersWithAccessDialog"
        @input="handleShowUsersWithAccessDialogInput"
        :title="$t('users.USERS_WITH_ACCESS').toString()"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            id="users-with-access-button"
            v-bind="attrs"
            small
            outlined
            color="primary"
            class="border-radius-8 padding-a-2 h-32 max-h-32"
            v-on="on"
          >
            <span class="d-flex align-center">
              <CUIcon view-box="0 0 24 24" class="w-16 h-16 margin-r-2">
                visibility
              </CUIcon>
              Users with Access
            </span>
          </v-btn>
        </template>
        <div class="padding-a-2">
          <ContactList
            id="share-quote-dialog-contact-list"
            :contact-groups="contactGroups"
            :align-left="false"
            :hide-edit="true"
          />
        </div>
      </DialogLayout>
      <ConfirmRemoveContactDialog
        :value="showRemoveContactDialog"
        @update:value="handleRemoveDialogInput"
        @cancel="handleRemoveContactCancel"
        @confirm="onRemoveContact"
        :contact-to-remove="contactToEdit"
        :contact-type="contactType"
        :entity-id="quoteId"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0, 0.1, 1);
}
.slide-left-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-40px);
}
.slide-left-enter-to,
.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0, 0.1, 1);
}
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-40px);
}
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
.slide-right-enter-to,
.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
